import MarkdownIt from 'markdown-it';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline.mjs';
import type StateBlock from 'markdown-it/lib/rules_block/state_block.mjs';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import { MarkdownParser } from 'prosemirror-markdown';
import { Node as PMNode } from 'prosemirror-model';
import { lapoSchema } from '../schema/schema.js';
import { PageType } from '../types.js';
import { coerceBookmarkMeta, type BookmarkMeta } from '../bookmark/index.js';

function isSpaceCode(code: number): boolean {
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d;
}

/** Inline rule: [[target]] or [[target|label]] -> wikilink token. */
function wikilinkRule(state: StateInline, silent: boolean): boolean {
  const start = state.pos;
  if (state.src.charCodeAt(start) !== 0x5b /* [ */) return false;
  if (state.src.charCodeAt(start + 1) !== 0x5b) return false;
  const end = state.src.indexOf(']]', start + 2);
  if (end < 0) return false;
  const inner = state.src.slice(start + 2, end);
  if (inner.length === 0 || inner.includes('[')) return false;
  if (!silent) {
    const pipe = inner.indexOf('|');
    const target = pipe >= 0 ? inner.slice(0, pipe).trim() : inner.trim();
    const label = pipe >= 0 ? inner.slice(pipe + 1).trim() : null;
    const token = state.push('wikilink', '', 0);
    token.meta = { target, label };
    token.content = inner;
  }
  state.pos = end + 2;
  return true;
}

/** Inline rule: #tag (preceded by start or whitespace). */
const TAG_RE = /^#([A-Za-z0-9_][A-Za-z0-9_/-]*)/;
function tagRule(state: StateInline, silent: boolean): boolean {
  const start = state.pos;
  if (state.src.charCodeAt(start) !== 0x23 /* # */) return false;
  if (start > 0) {
    const prev = state.src.charCodeAt(start - 1);
    if (!isSpaceCode(prev) && prev !== 0x28 /* ( */) return false;
  }
  const match = TAG_RE.exec(state.src.slice(start));
  if (!match) return false;
  if (!silent) {
    const token = state.push('tag', '', 0);
    token.meta = { name: match[1] };
    token.content = match[1]!;
  }
  state.pos = start + match[0].length;
  return true;
}

// LOOM initiative tag atom — `+[name](loom:ini_x@2026-Q1)` (quarter optional). Boundary-guarded like
// tags so `C++`/`a+b` never fire. The legacy bare `+name` mention rule is intentionally gone (LOOM
// D-13): only this explicit atom form creates a tag, so pre-LOOM `+mentions` parse as plain text.
const INITIATIVE_TAG_RE = /^\+\[([^\]]*)\]\(loom:([A-Za-z0-9_]+)(?:@(\d{4}-Q[1-4]))?\)/;
function initiativeTagRule(state: StateInline, silent: boolean): boolean {
  const start = state.pos;
  if (state.src.charCodeAt(start) !== 0x2b /* + */) return false;
  if (start > 0) {
    const prev = state.src.charCodeAt(start - 1);
    if (!isSpaceCode(prev) && prev !== 0x28 /* ( */) return false;
  }
  const match = INITIATIVE_TAG_RE.exec(state.src.slice(start));
  if (!match) return false;
  if (!silent) {
    const token = state.push('initiativeTag', '', 0);
    token.meta = { name: match[1], initiativeId: match[2], quarter: match[3] ?? null };
    token.content = match[1]!;
  }
  state.pos = start + match[0].length;
  return true;
}

/**
 * Core rule: split `((blockId))` out of text tokens into block_ref tokens (IMPL-07). Done as a core
 * rule, not an inline rule, because markdown-it's text rule does not treat `(` as a terminator (it
 * does for `[`/`#`/`{`), so an inline rule at `(` would never run. We post-process the inline
 * children instead. Only bare-id parens convert; ordinary `((x y))` prose is left untouched.
 */
const BLOCKREF_RE = /\(\(([A-Za-z0-9_-]+)\)\)/g;
function splitBlockRefs(state: StateCore): boolean {
  for (const blockTok of state.tokens) {
    if (blockTok.type !== 'inline' || !blockTok.children) continue;
    const out: typeof blockTok.children = [];
    for (const child of blockTok.children) {
      if (child.type !== 'text' || !child.content.includes('((')) {
        out.push(child);
        continue;
      }
      const text = child.content;
      let last = 0;
      let m: RegExpExecArray | null;
      let matched = false;
      BLOCKREF_RE.lastIndex = 0;
      while ((m = BLOCKREF_RE.exec(text))) {
        matched = true;
        if (m.index > last) {
          const t = new state.Token('text', '', 0);
          t.content = text.slice(last, m.index);
          out.push(t);
        }
        const ref = new state.Token('block_ref', '', 0);
        ref.meta = { blockId: m[1] };
        out.push(ref);
        last = m.index + m[0].length;
      }
      if (!matched) {
        out.push(child);
        continue;
      }
      if (last < text.length) {
        const t = new state.Token('text', '', 0);
        t.content = text.slice(last);
        out.push(t);
      }
    }
    blockTok.children = out;
  }
  return true;
}

/** Block rule: a line that is exactly `{{embed [[Page]]}}` or `{{embed ((id))}}` -> embed token. */
const EMBED_RE = /^\{\{embed\s+(?:\[\[([^\]]+)\]\]|\(\(([A-Za-z0-9_-]+)\)\))\s*\}\}$/;
function embedRule(state: StateBlock, startLine: number, _endLine: number, silent: boolean): boolean {
  const start = state.bMarks[startLine]! + state.tShift[startLine]!;
  const max = state.eMarks[startLine]!;
  const line = state.src.slice(start, max).trim();
  const m = EMBED_RE.exec(line);
  if (!m) return false;
  if (silent) return true;
  const token = state.push('embed', '', 0);
  token.meta = m[1] != null ? { kind: 'page', target: m[1].trim() } : { kind: 'block', target: m[2] };
  token.map = [startLine, startLine + 1];
  state.line = startLine + 1;
  return true;
}

/**
 * Core rule: lift a trailing block-meta comment off a paragraph/heading onto its open token. The
 * comment carries any of `id`, `due`, `scheduled` (block id + planning dates), e.g.
 * `<!-- id: aB3 due: 2026-10-05 scheduled: 2026-09-28 -->`. Back-compatible with the old id-only form.
 */
const META_TRAILER_RE = /\s*<!--\s*((?:(?:id|due|scheduled):\s*[A-Za-z0-9_-]+\s*)+)-->\s*$/;
function extractBlockMeta(state: StateCore): boolean {
  const toks = state.tokens;
  for (let i = 0; i < toks.length - 1; i++) {
    const open = toks[i]!;
    if (open.type !== 'paragraph_open' && open.type !== 'heading_open') continue;
    const inline = toks[i + 1]!;
    if (inline.type !== 'inline') continue;
    const m = META_TRAILER_RE.exec(inline.content);
    if (!m) continue;
    const meta: Record<string, string> = {};
    for (const pair of m[1]!.matchAll(/(id|due|scheduled):\s*([A-Za-z0-9_-]+)/g)) meta[pair[1]!] = pair[2]!;
    open.meta = { ...(open.meta as object), ...meta };
    inline.content = inline.content.replace(META_TRAILER_RE, '');
    // Keep the children consistent with content: strip the marker off the last text child.
    const kids = inline.children;
    if (kids) {
      for (let j = kids.length - 1; j >= 0; j--) {
        if (kids[j]!.type === 'text') {
          kids[j]!.content = kids[j]!.content.replace(META_TRAILER_RE, '');
          break;
        }
      }
    }
  }
  return true;
}

/** Inline rule: $latex$ (single $, not $$). */
function mathInlineRule(state: StateInline, silent: boolean): boolean {
  const start = state.pos;
  if (state.src.charCodeAt(start) !== 0x24 /* $ */) return false;
  if (state.src.charCodeAt(start + 1) === 0x24) return false; // $$ is block
  const max = state.posMax;
  let pos = start + 1;
  while (pos < max && state.src.charCodeAt(pos) !== 0x24) pos++;
  if (pos >= max || pos === start + 1) return false;
  if (!silent) {
    const token = state.push('math_inline', '', 0);
    token.content = state.src.slice(start + 1, pos);
  }
  state.pos = pos + 1;
  return true;
}

/** Block rule: $$ ... $$ math blocks. */
function mathBlockRule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
  const start = state.bMarks[startLine]! + state.tShift[startLine]!;
  const max = state.eMarks[startLine]!;
  if (start + 2 > max) return false;
  if (state.src.slice(start, start + 2) !== '$$') return false;

  const firstRest = state.src.slice(start + 2, max).trim();
  if (firstRest.endsWith('$$') && firstRest.length >= 2) {
    // single-line: $$ ... $$
    if (silent) return true;
    const token = state.push('math_block', '', 0);
    token.content = firstRest.slice(0, -2).trim();
    token.map = [startLine, startLine + 1];
    state.line = startLine + 1;
    return true;
  }
  if (firstRest !== '') return false; // not an opening fence we recognize

  // multi-line: collect until closing $$
  let nextLine = startLine + 1;
  const lines: string[] = [];
  let found = false;
  while (nextLine < endLine) {
    const ls = state.bMarks[nextLine]! + state.tShift[nextLine]!;
    const le = state.eMarks[nextLine]!;
    const lineText = state.src.slice(ls, le);
    if (lineText.trim() === '$$') {
      found = true;
      break;
    }
    lines.push(lineText);
    nextLine++;
  }
  if (!found) return false;
  if (silent) return true;
  const token = state.push('math_block', '', 0);
  token.content = lines.join('\n');
  token.map = [startLine, nextLine];
  state.line = nextLine + 1;
  return true;
}

/**
 * Block rule: `:::gallery` / `:::details` container directives (GRAM-21). Strict grammar — the
 * directive must open a line, only the two known names fire, and an unclosed or malformed body
 * falls through to normal parsing — so plain prose containing `:::` is unaffected (spec R6).
 *
 * `:::gallery columns=2 gap=8 fit=cover [rows=N]` bodies must be exclusively `![alt](src "title")`
 * lines (one per image; blanks allowed) and become imageGrid > image+. `:::details <summary>`
 * (optional trailing `{open}` = expanded) bodies are tokenized as ordinary blocks → disclosure.
 */
const DIRECTIVE_OPEN_RE = /^:::(gallery|details|bookmark)(?:\s+(.*?))?\s*$/;

/** Parse the `:::bookmark` body (a single JSON object); returns null when it's malformed/urlless. */
function parseBookmarkBody(body: string): BookmarkMeta | null {
  try {
    return coerceBookmarkMeta(JSON.parse(body.trim()) as Partial<BookmarkMeta>);
  } catch {
    return null;
  }
}
const GALLERY_IMAGE_RE = /^!\[([^\]]*)\]\(\s*(\S+?)(?:\s+"((?:[^"\\]|\\.)*)")?\s*\)$/;

function parseGalleryInfo(info: string): { columns: number; rows: number | null; gap: number; fit: string } {
  const attrs = { columns: 2, rows: null as number | null, gap: 8, fit: 'cover' };
  for (const m of info.matchAll(/(columns|rows|gap|fit)=([A-Za-z0-9]+)/g)) {
    const [, key, value] = m;
    if (key === 'fit') {
      if (value === 'cover' || value === 'contain') attrs.fit = value;
    } else if (/^\d+$/.test(value!)) {
      attrs[key as 'columns' | 'rows' | 'gap'] = +value!;
    }
  }
  return attrs;
}

function parseDetailsInfo(info: string): { summary: string; collapsed: boolean } {
  const collapsed = !/\{open\}\s*$/.test(info);
  const summary = info.replace(/\{open\}\s*$/, '').trim();
  return { summary: summary || 'Details', collapsed };
}

function directiveRule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
  if (state.sCount[startLine]! - state.blkIndent >= 4) return false; // indented code, not a directive
  const start = state.bMarks[startLine]! + state.tShift[startLine]!;
  const max = state.eMarks[startLine]!;
  const m = DIRECTIVE_OPEN_RE.exec(state.src.slice(start, max));
  if (!m) return false;

  // Find the matching closing `:::`, depth-aware so a gallery nested inside a details body
  // doesn't close the outer directive. Stops if the enclosing block's indentation ends first.
  let nextLine = startLine + 1;
  let closed = false;
  let depth = 0;
  for (; nextLine < endLine; nextLine++) {
    if (state.sCount[nextLine]! < state.blkIndent) break;
    const ls = state.bMarks[nextLine]! + state.tShift[nextLine]!;
    const le = state.eMarks[nextLine]!;
    const text = state.src.slice(ls, le).trim();
    if (DIRECTIVE_OPEN_RE.test(text)) {
      depth++;
    } else if (text === ':::') {
      if (depth === 0) {
        closed = true;
        break;
      }
      depth--;
    }
  }
  if (!closed) return false;

  const name = m[1] as 'gallery' | 'details' | 'bookmark';
  const info = m[2] ?? '';

  if (name === 'bookmark') {
    // Body is a single JSON object of the resolved link metadata (robust to spaces/special chars).
    const bodyLines: string[] = [];
    for (let line = startLine + 1; line < nextLine; line++) {
      const ls = state.bMarks[line]! + state.tShift[line]!;
      const le = state.eMarks[line]!;
      bodyLines.push(state.src.slice(ls, le));
    }
    const meta = parseBookmarkBody(bodyLines.join('\n'));
    if (!meta) return false; // malformed → fall through to plain parsing
    if (silent) return true;
    const tok = state.push('bookmark', '', 0);
    tok.meta = meta;
    tok.map = [startLine, nextLine];
    state.line = nextLine + 1;
    return true;
  }

  if (name === 'gallery') {
    // Body must be exclusively image lines; anything else means this isn't a gallery after all.
    const images: { alt: string; src: string; title: string | null }[] = [];
    for (let line = startLine + 1; line < nextLine; line++) {
      const ls = state.bMarks[line]! + state.tShift[line]!;
      const le = state.eMarks[line]!;
      const text = state.src.slice(ls, le).trim();
      if (!text) continue;
      const im = GALLERY_IMAGE_RE.exec(text);
      if (!im) return false;
      images.push({ alt: im[1]!, src: im[2]!, title: im[3] != null ? im[3].replace(/\\(.)/g, '$1') : null });
    }
    if (images.length === 0) return false; // imageGrid content is image+
    if (silent) return true;
    const open = state.push('gallery_open', 'div', 1);
    open.meta = parseGalleryInfo(info);
    open.map = [startLine, nextLine];
    for (const img of images) {
      const tok = state.push('image', 'img', 0);
      tok.attrSet('src', img.src);
      if (img.title) tok.attrSet('title', img.title);
      const alt = new state.Token('text', '', 0);
      alt.content = img.alt;
      tok.children = [alt];
      tok.content = img.alt;
    }
    state.push('gallery_close', 'div', -1);
    state.line = nextLine + 1;
    return true;
  }

  if (silent) return true;
  const open = state.push('details_open', 'div', 1);
  open.meta = parseDetailsInfo(info);
  open.map = [startLine, nextLine];
  const oldParent = state.parentType;
  const oldLineMax = state.lineMax;
  state.parentType = 'directive' as typeof state.parentType;
  state.lineMax = nextLine;
  state.md.block.tokenize(state, startLine + 1, nextLine);
  state.parentType = oldParent;
  state.lineMax = oldLineMax;
  state.push('details_close', 'div', -1);
  state.line = nextLine + 1;
  return true;
}

/** Core rule: retype ```mermaid fences and flatten table thead/tbody wrappers. */
function normalizeTokens(state: StateCore): boolean {
  for (const tok of state.tokens) {
    if (tok.type === 'fence' && tok.info.trim() === 'mermaid') {
      tok.type = 'mermaid';
    }
  }
  state.tokens = state.tokens.filter(
    (t) => !['thead_open', 'thead_close', 'tbody_open', 'tbody_close'].includes(t.type),
  );
  return true;
}

function buildMarkdownIt(): MarkdownIt {
  // html:true so block HTML (a pasted `<table>`/`<div>`) is captured as an html_block → `html`
  // node. Inline HTML stays literal text (the html_inline rule is disabled) so stray `<` in prose
  // and the closed inline contract are unaffected.
  const md = new MarkdownIt({ html: true, linkify: false, breaks: false });
  md.disable('strikethrough');
  md.inline.ruler.disable('html_inline');
  md.block.ruler.before('fence', 'math_block', mathBlockRule);
  md.block.ruler.before('fence', 'directive', directiveRule, {
    alt: ['paragraph', 'reference', 'blockquote', 'list'],
  });
  md.block.ruler.before('paragraph', 'embed', embedRule);
  md.inline.ruler.before('link', 'wikilink', wikilinkRule);
  md.inline.ruler.before('text', 'tag', tagRule);
  md.inline.ruler.before('text', 'initiativeTag', initiativeTagRule);
  md.inline.ruler.before('text', 'math_inline', mathInlineRule);
  md.core.ruler.push('lapo_block_ids', extractBlockMeta);
  md.core.ruler.push('lapo_block_refs', splitBlockRefs);
  md.core.ruler.push('lapo_normalize', normalizeTokens);
  return md;
}

const md = buildMarkdownIt();

// Token -> node/mark map for the Lapo schema (camelCase node names).
interface BlockMetaTok {
  meta?: { id?: string; due?: string; scheduled?: string };
}

const tokenSpec = {
  blockquote: { block: 'blockquote' },
  paragraph: {
    block: 'paragraph',
    getAttrs: (tok: BlockMetaTok) => ({ id: tok.meta?.id ?? null, due: tok.meta?.due ?? null, scheduled: tok.meta?.scheduled ?? null }),
  },
  list_item: { block: 'listItem' },
  bullet_list: { block: 'bulletList' },
  ordered_list: {
    block: 'orderedList',
    getAttrs: (tok: { attrGet(n: string): string | null }) => ({ order: +(tok.attrGet('start') || 1) }),
  },
  heading: {
    block: 'heading',
    getAttrs: (tok: BlockMetaTok & { tag: string }) => ({
      level: +tok.tag.slice(1),
      id: tok.meta?.id ?? null,
      due: tok.meta?.due ?? null,
      scheduled: tok.meta?.scheduled ?? null,
    }),
  },
  code_block: { block: 'codeBlock', noCloseToken: true },
  fence: {
    block: 'codeBlock',
    getAttrs: (tok: { info: string }) => ({ language: tok.info.trim() || null }),
    noCloseToken: true,
  },
  mermaid: { block: 'mermaid', noCloseToken: true },
  math_block: { block: 'mathBlock', noCloseToken: true },
  html_block: { node: 'html', getAttrs: (tok: { content: string }) => ({ html: tok.content.replace(/\n+$/, '') }) },
  hr: { node: 'horizontalRule' },
  image: {
    node: 'image',
    getAttrs: (tok: { attrGet(n: string): string | null; children?: Array<{ content: string }> }) => ({
      src: tok.attrGet('src') || '',
      title: tok.attrGet('title') || null,
      alt: tok.children?.[0]?.content || null,
    }),
  },
  hardbreak: { node: 'hardBreak' },
  table: { block: 'table' },
  tr: { block: 'tableRow' },
  th: { block: 'tableCell', getAttrs: () => ({ header: true }) },
  td: { block: 'tableCell', getAttrs: () => ({ header: false }) },
  em: { mark: 'em' },
  strong: { mark: 'strong' },
  s: { mark: 'em' }, // safety: shouldn't appear (disabled), map to em if it does
  link: {
    mark: 'link',
    getAttrs: (tok: { attrGet(n: string): string | null }) => ({
      href: tok.attrGet('href') || '',
      title: tok.attrGet('title') || null,
    }),
  },
  code_inline: { mark: 'code', noCloseToken: true },
  wikilink: {
    node: 'wikilink',
    getAttrs: (tok: { meta: { target: string; label: string | null } }) => ({
      target: tok.meta.target,
      label: tok.meta.label,
    }),
  },
  tag: { node: 'tag', getAttrs: (tok: { meta: { name: string } }) => ({ name: tok.meta.name }) },
  initiativeTag: {
    node: 'initiativeTag',
    getAttrs: (tok: { meta: { name: string; initiativeId: string; quarter: string | null } }) => ({
      name: tok.meta.name,
      initiativeId: tok.meta.initiativeId,
      quarter: tok.meta.quarter,
    }),
  },
  block_ref: { node: 'blockRef', getAttrs: (tok: { meta: { blockId: string } }) => ({ blockId: tok.meta.blockId }) },
  embed: {
    node: 'embed',
    getAttrs: (tok: { meta: { kind: string; target: string } }) => ({ kind: tok.meta.kind, target: tok.meta.target }),
  },
  math_inline: { node: 'mathInline', getAttrs: (tok: { content: string }) => ({ latex: tok.content }) },
  gallery: {
    block: 'imageGrid',
    getAttrs: (tok: { meta: { columns: number; rows: number | null; gap: number; fit: string } }) => tok.meta,
  },
  details: {
    block: 'disclosure',
    getAttrs: (tok: { meta: { summary: string; collapsed: boolean } }) => tok.meta,
  },
  bookmark: { node: 'bookmark', getAttrs: (tok: { meta: BookmarkMeta }) => tok.meta },
};

export const lapoMarkdownParser = new MarkdownParser(lapoSchema, md, tokenSpec as never);

export interface ParseOptions {
  pageType?: PageType;
}

/**
 * Parse markdown into a ProseMirror document (IMPL-01).
 * Pure and deterministic. Empty input yields a doc with a single empty paragraph
 * so the `block+` content constraint always holds.
 */
export function parseFromMarkdown(markdown: string, opts: ParseOptions = {}): PMNode {
  let doc = lapoMarkdownParser.parse(markdown);
  if (!doc || doc.childCount === 0) {
    doc = lapoSchema.node('doc', null, [lapoSchema.node('paragraph')]);
  }
  if (opts.pageType && doc.attrs.pageType !== opts.pageType) {
    doc = lapoSchema.node('doc', { ...doc.attrs, pageType: opts.pageType }, doc.content);
  }
  return doc;
}
